/**
 * OverviewHome.tsx — Main dashboard landing page (S-Tier polish, §11 O-1).
 *
 * All data is live: metric cards + deals-by-stage from /v1/sales/stats, the
 * deals table from /v1/sales/deals, contacts from /v1/sales/contacts, the
 * activity feed from /v1/sales/activities, and the pending-approval strip from
 * /v1/approvals. The prototype's fabricated email funnel / deal-growth chart
 * have been removed (no backing endpoint).
 *
 * §11 honesty fixes: the stage chart renders a FIXED zero-filled open-stage
 * frame with STAGE-KEYED fills (overviewView.ts) — the raw dealsByStage
 * aggregate carries closed stages and drops empty ones, and index-bound
 * fills reshuffle as stages appear. Cards are value-only (O11-6): a future
 * delta line slots under each value without re-layout.
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { useState } from 'react'
import { Search } from 'lucide-react'
import { Avatar, DealStageBadge, LoadingState, ErrorState, EmptyState } from '../DashboardShared'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts'
import { useAsync, fetchSalesSummary, fetchContacts, fetchApprovals } from '../api'
import { openStageValueFrame } from './overviewView'
import ApprovalDrawer from '../ApprovalDrawer'
import type { DashApproval } from '../types'
import { ink, edge, shadow, surface } from '../../../theme/tokens'

// Takes integer PENCE (the summary/deal money unit since the D9 switch).
function fmtMoney(pence: number) {
  const n = pence / 100
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `£${(n / 1_000).toFixed(0)}k`
  return `£${n.toFixed(0)}`
}

/**
 * The k-metric widget for the workspace/platform pages (S-Tier slice 6) --
 * the OpsMetric/SalesMetric/FinanceMetric/HRMetric precedent, closing the
 * set: markup-level reuse living with these pages; DashboardShared's
 * MetricCard is now consumer-less but stays untouched (its removal is MC-arc
 * housekeeping). Value-only -- no honest delta exists for any of these.
 */
export function WorkspaceMetric({ label, value, note }: { label: string; value: string | number; note?: string }) {
  return (
    <div className="k-metric">
      <div className="flex items-center justify-between gap-2">
        <span className="k-metric-label uppercase">{label}</span>
      </div>
      <div className="flex flex-col gap-[7px]">
        <div className="flex items-baseline gap-2">
          <span className="k-metric-value">{value}</span>
        </div>
        {note && <div className="k-metric-note">{note}</div>}
      </div>
    </div>
  )
}

export function OverviewHome({ onNavigate, onSelectDeal, canDecide }: {
  onNavigate: (page: string) => void
  /** Opens a deal in Deal Detail (the table rows' promised affordance, O11-4). */
  onSelectDeal?: (id: string) => void
  canDecide: boolean
}) {
  const [dealSearch, setDealSearch] = useState('')
  const [selectedApproval, setSelectedApproval] = useState<DashApproval | null>(null)
  // Bumped after an approval decision so useAsync re-runs and the actioned
  // approval drops off the pending list.
  const [refreshKey, setRefreshKey] = useState(0)
  const { data, loading, error } = useAsync(
    () => Promise.all([fetchSalesSummary(), fetchContacts(), fetchApprovals()]),
    [refreshKey],
  )

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const [{ summary, deals, activities }, contacts, approvals] = data!

  const filteredDeals = deals.filter(d =>
    dealSearch === '' || d.name.toLowerCase().includes(dealSearch.toLowerCase()) ||
    d.owner.toLowerCase().includes(dealSearch.toLowerCase()),
  )

  // Value-only cards (O11-1: no icons; O11-6: no deltas — the slot stays open).
  const METRICS = [
    { label: 'Total Pipeline', value: fmtMoney(summary.totalPipeline) },
    // 2.2 (P2-8): from /v1/sales/reports (effective-probability weighting),
    // not the /stats float; absent on a pre-2.2 backend → "—".
    { label: 'Weighted Pipeline', value: summary.weightedPipeline == null ? '—' : fmtMoney(summary.weightedPipeline) },
    { label: 'Open Deals', value: summary.openDeals },
    { label: 'Closed Won', value: fmtMoney(summary.closedWon) },
    { label: 'Total Contacts', value: contacts.length },
    { label: 'Win Rate', value: summary.dealsWinRate },
  ]

  // The chart's honest frame: fixed open stages, zero-filled, stage-keyed fills.
  const stageFrame = openStageValueFrame(summary.dealsByStage)
  const chartEmpty = stageFrame.every(s => s.value === 0)

  return (
    <div className="page-enter flex flex-col gap-4">
      <div className="grid grid-cols-[1fr_240px] gap-4">

        {/* LEFT */}
        <div className="flex flex-col gap-4">
          {/* 6 metric cards — 3-up, overline labels, value-only */}
          <div className="grid grid-cols-3 gap-3">
            {/* delta slot (O11-6) still open: k-metric is value-only here -- a
                future delta line inserts as k-metric-delta without re-layout */}
            {METRICS.map(m => <WorkspaceMetric key={m.label} label={String(m.label)} value={m.value} />)}
          </div>

          {/* Deals by stage — fixed open-stage frame (see overviewView.ts) */}
          <div className="k-card">
            <div className="k-card-head">
              <div>
                <div className="k-card-title">Pipeline by stage</div>
                <div className="k-card-sub">Value of open deals by stage</div>
              </div>
            </div>
            <div className="p-4">
              {chartEmpty ? (
                <EmptyState message="No open deals in the pipeline yet" />
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={stageFrame}>
                    <CartesianGrid strokeDasharray="3 3" stroke={edge['row-faint']} vertical={false} />
                    <XAxis dataKey="stage" tick={{ fontSize: 10, fill: ink.faint }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: ink.faint }} axisLine={false} tickLine={false} tickFormatter={v => fmtMoney(v)} />
                    <Tooltip contentStyle={{ background: surface.card, border: 'none', borderRadius: 10, fontSize: 12, boxShadow: shadow.tooltip }} formatter={(v: number) => fmtMoney(v)} cursor={{ fill: surface['row-hover'] }} />
                    <Bar dataKey="value" name="Value" radius={[4, 4, 0, 0]}>
                      {stageFrame.map(s => <Cell key={s.stage} fill={s.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Deals table */}
          <div className="k-card">
            <div className="k-card-head">
              <div className="k-card-title">Deals</div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
                  <input
                    value={dealSearch}
                    onChange={e => setDealSearch(e.target.value)}
                    placeholder="Search name, owner…"
                    className="h-7 pl-7 pr-3 text-12 bg-surface-input border border-edge-control rounded-control text-ink-body placeholder:text-ink-faint w-48"
                  />
                </div>
                <button className="k-btn-secondary" onClick={() => onNavigate('sales.pipeline')}>
                  View all Deals
                </button>
              </div>
            </div>
            {filteredDeals.length === 0 ? (
              <EmptyState message="No deals found" />
            ) : (
              <table className="closr-table dash-table w-full">
                <thead>
                  <tr>
                    <th className="text-left">Deal Name</th>
                    <th className="text-left">Deal Owner</th>
                    <th className="text-left">Amount</th>
                    <th className="text-left">Close Date</th>
                    <th className="text-left">Deal Stage</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDeals.slice(0, 10).map(deal => (
                    <tr key={deal.id} className="cursor-pointer hover:bg-surface-row-hover transition-colors"
                      onClick={() => onSelectDeal?.(deal.id)}>
                      <td className="font-medium text-ink-primary">{deal.name}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <Avatar name={deal.owner} size={22} />
                          <span className="text-13 text-ink-body">{deal.owner}</span>
                        </div>
                      </td>
                      <td className="font-semibold text-ink-primary">{fmtMoney(deal.amount)}</td>
                      <td className="text-12 text-ink-secondary">{deal.closeDate}</td>
                      <td><DealStageBadge stage={deal.stage} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* RIGHT: Approvals + Contacts + Activity */}
        <div className="flex flex-col gap-4">
          {/* Pending approvals */}
          <div className="k-card">
            <div className="k-card-head">
              <div className="k-card-title">Pending Approvals</div>
              <button className="k-card-link cursor-pointer bg-transparent border-0 p-0 uppercase hover:underline"
                onClick={() => onNavigate('finance.approvals')}>See All</button>
            </div>
            <div className="divide-y divide-edge-row-faint">
              {approvals.length === 0 ? (
                <div className="px-4 py-6 text-center text-12 text-ink-muted">No pending approvals</div>
              ) : (
                approvals.slice(0, 5).map(a => (
                  <div key={a.id} role="button" tabIndex={0}
                    onClick={() => setSelectedApproval(a)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedApproval(a) } }}
                    className="px-4 py-3 hover:bg-surface-row-hover transition-colors cursor-pointer focus:outline-none ">
                    {/* skill falls back to '—' upstream when the gate has no
                        skillId — render the run identity instead of a bare dash */}
                    <div className="text-12 font-medium text-ink-primary leading-tight">{a.skill === '—' ? `Run ${a.runId.slice(0, 8)}` : a.skill}</div>
                    <div className="text-11 text-ink-faint truncate">{a.summary}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Contacts */}
          <div className="k-card">
            <div className="k-card-head">
              <div className="k-card-title">Contacts</div>
              <button className="k-card-link cursor-pointer bg-transparent border-0 p-0 uppercase hover:underline"
                onClick={() => onNavigate('sales.contacts')}>See All</button>
            </div>
            <div className="divide-y divide-edge-row-faint">
              {contacts.length === 0 ? (
                <div className="px-4 py-6 text-center text-12 text-ink-muted">No contacts yet</div>
              ) : (
                contacts.slice(0, 6).map(c => (
                  <div key={c.id} className="flex items-center justify-between px-4 py-3 hover:bg-surface-row-hover transition-colors">
                    <div className="flex items-center gap-3">
                      <Avatar name={c.name} size={28} />
                      <div>
                        <div className="text-13 font-medium text-ink-primary leading-tight">{c.name}</div>
                        <div className="text-11 text-ink-faint">{c.lastContacted}</div>
                      </div>
                    </div>
                    <button className="text-11 text-accent font-medium hover:underline" onClick={() => onNavigate('sales.contacts')}>View</button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Activity feed */}
          <div className="k-card">
            <div className="k-card-head">
              <div className="k-card-title">Activity</div>
              <button className="k-card-link cursor-pointer bg-transparent border-0 p-0 uppercase hover:underline"
                onClick={() => onNavigate('sales.activities')}>See All</button>
            </div>
            <div className="divide-y divide-edge-row-faint">
              {activities.length === 0 ? (
                <div className="px-4 py-6 text-center text-12 text-ink-muted">No recent activity</div>
              ) : (
                activities.slice(0, 5).map(a => (
                  <div key={a.id} className="px-4 py-3 hover:bg-surface-row-hover transition-colors">
                    <div className="flex items-start gap-2">
                      <div className={`mt-2 flex-shrink-0 w-1.5 h-1.5 rounded-full ${a.type === 'email' ? 'bg-accent' : a.type === 'call' ? 'bg-success' : a.type === 'meeting' ? 'bg-info' : 'bg-warning'}`} />
                      <div className="flex-1 min-w-0">
                        {/* "<subject> — <body>", as on the Sales dashboard. */}
                        <p className="text-12 text-ink-body leading-snug">
                          <span className="font-semibold text-ink-primary">{a.subject}</span>
                          {a.description ? ` — ${a.description}` : ''}
                        </p>
                        <div className="text-overline text-ink-faint mt-1">{a.time}</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {selectedApproval && (
        <ApprovalDrawer
          approval={selectedApproval}
          onClose={() => setSelectedApproval(null)}
          onDecided={() => { setSelectedApproval(null); setRefreshKey(k => k + 1) }}
          canDecide={canDecide}
        />
      )}
    </div>
  )
}
